/**
 * This file is largely copied from attachButtonMenuItemBuilder.tsx and attachButtonMenuItemBuilderUtils.ts
 * in owa-mail-compose-view.
 * We are copying these files because this feature is still in a volatile state, so we wish to avoid
 * attempting to refactor the code so that it can be used here.
 * There is also ongoing experimentation with the Attachment Suggestions feature.
 */
import { getRTL } from '@fluentui/react/lib/Utilities';
import { formatUserDateTime, owaDate, OwaDate } from 'owa-datetime';
import { CloudIconWithOverlay } from 'owa-file-icon';
import loc, { format } from 'owa-localize';
import { FeatureNames } from 'owa-mail-smart-pill-features';
import { ClientItem } from 'owa-mail-store';
import { ReplyWithAttachmentSuggestion, ReplyWithEndpointType } from 'owa-attachment-suggestions';
import {
    logOnClickDatapoint,
    logOnClickDatapointLegacy,
    SmartPillLogData,
    SMART_DOC_MENU_CLICKED,
} from 'owa-smart-pill';
import { SmartDocOption } from 'owa-mail-smart-pill-features';
import * as trace from 'owa-trace';
import * as React from 'react';
import {
    smartDocLastModifiedText,
    smartDocAriaText,
    smartDocAccessedOnText,
} from './AttachmentSuggestionButton.locstring.json';
import { formatPastRelativeDateTime } from 'owa-datetime';
import { observableNow } from 'owa-observable-datetime';

const TRUNCATION_SEPARATOR: string = '...';
const MAX_FILENAME_LENGTH: number = 30;
const styles = require('./AttachmentSuggestionButton.scss');

export interface AttachmentSuggestionButtonProps {
    item: ClientItem;
    suggestion: ReplyWithAttachmentSuggestion;
    smartPillLogData: SmartPillLogData;
    onAttachmentClicked: (
        item: ClientItem,
        suggestion: ReplyWithAttachmentSuggestion,
        option: SmartDocOption
    ) => void;
    dismissMenu: (ev?: any, dismissAll?: boolean) => void;
}

export default function AttachmentSuggestionButton(props: AttachmentSuggestionButtonProps) {
    const { item, suggestion, smartPillLogData, onAttachmentClicked, dismissMenu } = props;

    const onClickCallback = React.useCallback(() => {
        // The pill framework (SmartPill.tsx) would normally handle this logic, but it currently
        // can't intercept the onClick handler for a pill's submenu, so we must duplicate the logic here.

        logOnClickDatapoint(SMART_DOC_MENU_CLICKED, smartPillLogData, {
            subtype: 'FileSuggestion' + suggestion.Position,
            clickedSuggestion: {
                endpoint: ReplyWithEndpointType[suggestion.Endpoint],
                fileSourceType: suggestion.FileSourceType,
            },
        });

        // For now, just log a legacy datapoint so that the existing smart reply scorecards are not broken.
        // However, we do need a new scorecard for the newer datapoints added.
        logOnClickDatapointLegacy(
            smartPillLogData.mainPayload.EID,
            smartPillLogData.mainPayload.position,
            smartPillLogData.mainPayload.loggableText,
            FeatureNames.SmartDoc,
            smartPillLogData.mainPayload.language,
            smartPillLogData.mainPayload.locale,
            smartPillLogData.cosmosOnlyData
        );

        onAttachmentClicked(item, suggestion, SmartDocOption.FileSuggestion);

        dismissMenu();
    }, [item, suggestion, dismissMenu]);

    let toolTip: string;
    let secondaryText: string;
    let truncatedFileName: string;

    // Protect with try/catch these functions parse data that may not exist in the format we expect.
    try {
        toolTip = getTooltipForAttachmentSuggestion(suggestion);
        secondaryText = getSecondaryTextForAttachmentSuggestion(suggestion);
        truncatedFileName = middleTruncateFileName(
            fixFileName(suggestion) + getFileExtension(suggestion)
        );
    } catch (e) {
        trace.trace.warn(`[SmartDocMenuItemBuilder] Error: ${e.message}`);
        (e as { networkError?: boolean }).networkError = true;
        throw e;
    }

    return (
        <button
            className={styles.attachmentSuggestionButton}
            title={toolTip}
            name={suggestion.FileName}
            aria-label={format(loc(smartDocAriaText), suggestion.FileName)}
            aria-posinset={1}
            aria-setsize={7}
            aria-disabled="false"
            role="menuitem"
            onClick={onClickCallback}
            tabIndex={0}>
            <div className={styles.attachmentSuggestionButtonContent}>
                <div
                    className={styles.attachmentSuggestionButtonImageContainer}
                    role="presentation"
                    aria-hidden="true">
                    {getIconForSuggestedAttachment(suggestion)}
                </div>
                <div>
                    <div className={styles.attachmentSuggestionButtonTextContainer}>
                        <div className={styles.attachmentSuggestionPrimaryText}>
                            {truncatedFileName}
                        </div>
                    </div>
                    <div className={styles.attachmentSuggestionSecondaryText}>{secondaryText}</div>
                </div>
            </div>
        </button>
    );
}

function getAccessDate(suggestion: ReplyWithAttachmentSuggestion): string {
    if (
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassic ||
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassicContextual
    ) {
        return null;
    } else {
        const { DateAccessed: dateAccessed } = suggestion;

        if (!dateAccessed) {
            trace.errorThatWillCauseAlert(
                `[SmartDocMenuItemBuilder] Access date is not available for suggestion "${suggestion.Id}"`
            );
        }

        const date: OwaDate = owaDate('UTC', dateAccessed);
        const dateAccessedText = formatPastRelativeDateTime(date, observableNow());

        return format(loc(smartDocAccessedOnText), dateAccessedText);
    }
}

function getModifiedDate(suggestion: ReplyWithAttachmentSuggestion): string {
    const { DateModified: dateModified } = suggestion;

    if (!dateModified) {
        trace.errorThatWillCauseAlert(
            `[SmartDocMenuItemBuilder] Modified date is not available for suggestion "${suggestion.Id}"`
        );
    }

    const date: OwaDate = owaDate('UTC', dateModified);
    const lastModifiedDateText: string = formatUserDateTime(date);

    return format(loc(smartDocLastModifiedText), lastModifiedDateText);
}

/**
 * For cloudy attachments, we are parsing a sharepoint or onedrive URL.
 * For classic attachments, we are parsing an email folder path ('\Inbox\Address to me' for example)
 */
function getBreadcrumbedFileOrEmailPath(suggestion: ReplyWithAttachmentSuggestion): string {
    let breadcrumbedFileOrEmailPath: string = null;

    // To parse URL for cloudy attachment, create temporary anchor tag.
    const tempAnchorTag: HTMLAnchorElement = document.createElement('a');
    tempAnchorTag.href = suggestion.Url;

    // Hostname should be something like sharepoint.microsoft.com
    // Pathname should be something like /documents/file.docx
    // Splitting the pathname using / as the delimiter, we can ignore the first and last tokens,
    // as the file path does not include the name of the file itself.
    breadcrumbedFileOrEmailPath = decodeURIComponent(tempAnchorTag.hostname);
    const isRTL: boolean = getRTL();

    const pathTokens: string[] = decodeURIComponent(tempAnchorTag.pathname).split('/');
    for (let i = 1; i < pathTokens.length - 1; i++) {
        if (isRTL) {
            // RTL
            breadcrumbedFileOrEmailPath = pathTokens[i] + ' < ' + breadcrumbedFileOrEmailPath;
        } else {
            // LTR
            breadcrumbedFileOrEmailPath += ' > ' + pathTokens[i];
        }
    }

    return breadcrumbedFileOrEmailPath;
}

function getSecondaryTextForAttachmentSuggestion(suggestion: ReplyWithAttachmentSuggestion) {
    if (
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassic ||
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassicContextual
    ) {
        return getBreadcrumbedFileOrEmailPath(suggestion);
    } else {
        return getAccessDate(suggestion);
    }
}

export function getTooltipForAttachmentSuggestion(
    suggestion: ReplyWithAttachmentSuggestion
): string {
    let tooltipString: string = null;

    // Protect with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        tooltipString = [
            suggestion.FileName,
            getBreadcrumbedFileOrEmailPath(suggestion),
            getAccessDate(suggestion),
            getModifiedDate(suggestion),
        ]
            .filter(str => str)
            .join('\n');
    } catch (e) {
        trace.trace.info(
            '[SmartDocMenuItemBuilder] Failed to parse attachment tooltip data, message: ' +
                e.message
        );
    } finally {
        return tooltipString;
    }
}

function getIconForSuggestedAttachment(suggestion: ReplyWithAttachmentSuggestion): JSX.Element {
    const nameWithExtension = suggestion.FileName + getFileExtension(suggestion);
    return (
        <CloudIconWithOverlay
            fileName={nameWithExtension}
            contentType={null}
            isBlocked={false}
            isFolder={false}
            embeddedItemClass={null}
            cloudyIconStyle={styles.cloudyIcon}
            fileIconStyle={styles.fileIcon}
        />
    );
}

function middleTruncateFileName(fileName: string) {
    // Do not truncate the string if it contains 30 characters or fewer.
    if (fileName.length <= MAX_FILENAME_LENGTH) {
        return fileName;
    } else {
        const charsToShow: number = MAX_FILENAME_LENGTH - TRUNCATION_SEPARATOR.length;
        const frontChars: number = Math.ceil(charsToShow / 2);
        const backChars: number = Math.floor(charsToShow / 2);

        const firstPartOfFileName: string = fileName.substr(0, frontChars);
        const lastPartOfFileName: string = fileName.substr(fileName.length - backChars);

        return `${firstPartOfFileName}${TRUNCATION_SEPARATOR}${lastPartOfFileName}`;
    }
}

function fixFileName(suggestion: ReplyWithAttachmentSuggestion): string {
    // If file name contains file extension, remove it. We'll add it separately.
    const fileExtension: string = '.' + suggestion.FileExtension;
    if (suggestion.FileName.indexOf(fileExtension) >= 0) {
        return suggestion.FileName.replace(fileExtension, '');
    } else {
        return suggestion.FileName;
    }
}

function getFileExtension(suggestion: ReplyWithAttachmentSuggestion) {
    return '.' + suggestion.FileExtension;
}
